import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "../../../ui/dialog";
import { EditorField } from "../../../ui/EditorField";

/** What the outer walls enclose, for calibrating by total floor area. */
export interface BuildingFootprint {
  areaPx: number;
  rooms: number;
}

export interface MeasurementPromptProps {
  /** Length of the measured line; absent when opened without a line. */
  pixelLength?: number;
  onConfirm: (meters: number) => void;
  onCancel: () => void;
  /** Enclosed floor area from the walls; enables "set from building
   *  area" when `onConfirmArea` is given too. */
  footprint?: BuildingFootprint | null;
  onConfirmArea?: (squareMeters: number) => void;
  pixelsPerMeter?: number | null;
}

const isPositive = (value: string): number | null => {
  const parsed = Number(value);
  return value.trim() && Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Calibrates the map's scale in-app instead of through a blocking
 * `window.prompt`: from a measured line's real length (same math as the
 * wall panel's "Real-world length" field), or from the building's total
 * floor area when the walls enclose one.
 */
export const MeasurementPrompt: React.FC<MeasurementPromptProps> = ({
  pixelLength,
  onConfirm,
  onCancel,
  footprint,
  onConfirmArea,
  pixelsPerMeter
}) => {
  const [meters, setMeters] = React.useState("");
  const [squareMeters, setSquareMeters] = React.useState("");
  const hasLine = pixelLength !== undefined;
  const hasFootprint = !!footprint && footprint.areaPx > 0 && !!onConfirmArea;

  const handleConfirm = () => {
    const parsed = isPositive(meters);
    if (parsed !== null) onConfirm(parsed);
  };
  const handleConfirmArea = () => {
    const parsed = isPositive(squareMeters);
    if (parsed !== null) onConfirmArea?.(parsed);
  };
  const footprintSqm =
    footprint && pixelsPerMeter ? footprint.areaPx / pixelsPerMeter ** 2 : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle>Set Map Scale</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {pixelsPerMeter ? (
            <p className="text-[11px] text-neutral-400">
              Current scale: {pixelsPerMeter.toFixed(1)} px per metre.
            </p>
          ) : null}
          {hasLine ? (
            <>
              <p className="text-neutral-500 text-xs">
                This line is {Math.round(pixelLength)}px long. Enter its
                real-world length to calibrate the map's scale.
              </p>
              <EditorField
                label="Real-world length (m)"
                type="number"
                min={0}
                step="any"
                autoFocus
                value={meters}
                onChange={(e) => setMeters(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleConfirm();
                }}
                placeholder="e.g. 4.5"
              />
            </>
          ) : null}
          {hasLine && hasFootprint ? (
            <p className="text-center text-[11px] text-neutral-400">or</p>
          ) : null}
          {hasFootprint && footprint ? (
            <>
              <p className="text-neutral-500 text-xs">
                The walls enclose {footprint.rooms} room
                {footprint.rooms === 1 ? "" : "s"}
                {footprintSqm !== null
                  ? `, ${footprintSqm.toFixed(1)} m² at the current scale`
                  : ""}
                . Enter the building's total floor area to set the scale from
                it.
              </p>
              <EditorField
                label="Building area (m²)"
                type="number"
                min={0}
                step="any"
                autoFocus={!hasLine}
                value={squareMeters}
                onChange={(e) => setSquareMeters(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleConfirmArea();
                }}
                placeholder="e.g. 850"
                data-building-area
              />
            </>
          ) : null}
          {!hasLine && !hasFootprint ? (
            <p className="text-neutral-500 text-xs">
              Draw the outer walls of the building to set the scale from its
              floor area, or measure a known length with the Measure tool.
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md px-3 py-1.5 text-neutral-600 text-xs hover:bg-neutral-100"
            >
              Cancel
            </button>
            {hasFootprint ? (
              <button
                type="button"
                onClick={handleConfirmArea}
                data-building-area-set
                className="rounded-md bg-blue-600 px-3 py-1.5 text-white text-xs hover:bg-blue-700"
              >
                Set from area
              </button>
            ) : null}
            {hasLine ? (
              <button
                type="button"
                onClick={handleConfirm}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-white text-xs hover:bg-blue-700"
              >
                Set
              </button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
